import * as alerts from '@redux/utils/alerts';
import { appIntl } from 'utility/context/IntlGlobalProvider';

export type PremiumFeature =
  | 'useWorkplan'
  | 'useLogisticRoute'
  | 'useMaintenance'
  | 'useFuelTelemetry'
  | 'useCostometer'
  | 'useObd2Telemetry'
  | 'useTemperatureTelemetry'
  | 'useVideo';

const FEATURE_INTL_KEYS: Record<PremiumFeature, string> = {
  useWorkplan: 'serviceClasses.useWorkplan',
  useLogisticRoute: 'serviceClasses.useLogisticRoute',
  useMaintenance: 'serviceClasses.useMaintenance',
  useFuelTelemetry: 'serviceClasses.useFuelTelemetry',
  useCostometer: 'serviceClasses.useCostometer',
  useObd2Telemetry: 'serviceClasses.useObd2Telemetry',
  useTemperatureTelemetry: 'serviceClasses.useTemperatureTelemetry',
  useVideo: 'serviceClasses.useVideo'
};

/**
 * Returns the display name of a premium feature from the intl catalog.
 */
export const getPremiumFeatureName = (feature: PremiumFeature): string => {
  const intl = appIntl();
  return intl.formatMessage({ id: FEATURE_INTL_KEYS[feature] });
};

/**
 * Checks whether a single unit has the required premium feature enabled.
 * Does NOT show any alert - use this for filtering lists silently.
 * Returns true when the unit has the feature (or no unit is provided).
 */
export const hasUnitPremiumFeature = (
  unit: any,
  feature: PremiumFeature
): boolean => {
  if (!unit) return true;

  // Try camelCase first (e.g., useWorkplan), then PascalCase (e.g., UseWorkplan)
  const pascalCaseFeature = feature.charAt(0).toUpperCase() + feature.slice(1);
  const featureValue = unit[feature] ?? unit[pascalCaseFeature];

  return featureValue === 1;
};

/**
 * Checks whether a single unit has the required premium feature enabled.
 * Shows a blocking alert if the feature is not available and returns false.
 * Returns true when the unit has the feature (or no unit is provided).
 * Use hasUnitPremiumFeature for silent checks without alerts.
 */
export const checkUnitPremiumFeature = (
  unit: any,
  feature: PremiumFeature
): boolean => {
  if (!unit) return true;

  // Try camelCase first (e.g., useWorkplan), then PascalCase (e.g., UseWorkplan)
  const pascalCaseFeature = feature.charAt(0).toUpperCase() + feature.slice(1);
  const featureValue = unit[feature] ?? unit[pascalCaseFeature];

  if (featureValue === 1) return true;

  const intl = appIntl();
  const featureName = getPremiumFeatureName(feature);
  const unitName = unit.unitName ?? intl.formatMessage({ id: 'common.unit' });

  alerts.showErrorAlertFailChanges({
    error: {
      errorMessage: intl.formatMessage(
        { id: 'serviceClasses.premiumFeatureNotIncluded' },
        { unitName, featureName }
      )
    }
  });

  return false;
};

/**
 * Checks whether ALL units in a list have the required premium feature.
 * Shows a single blocking alert listing the restricted units and returns false
 * if at least one unit is missing the feature. Returns true otherwise.
 */
export const checkMultipleUnitsPremiumFeature = (
  units: any[],
  feature: PremiumFeature
): boolean => {
  if (!units || units.length === 0) return true;

  // Try camelCase first, then PascalCase
  const pascalCaseFeature = feature.charAt(0).toUpperCase() + feature.slice(1);

  const restrictedUnits = units.filter((u) => {
    const featureValue = u[feature] ?? u[pascalCaseFeature];
    return featureValue !== 1;
  });

  if (restrictedUnits.length === 0) return true;

  const intl = appIntl();
  const featureName = getPremiumFeatureName(feature);
  const unitNames = restrictedUnits.map((u) => u.unitName ?? String(u.unitId)).join(', ');

  alerts.showErrorAlertFailChanges({
    error: {
      errorMessage: intl.formatMessage(
        { id: 'serviceClasses.premiumFeatureNotIncludedMultiple' },
        { unitNames, featureName }
      )
    }
  });

  return false;
};
